import {
  Button,
  Card,
  Container,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import FromSection from "screens/Campaigns/CreateCampaign/components/FromSection";
import SubjectSection from "screens/Campaigns/CreateCampaign/components/SubjectSection";
import RecipientsSection from "screens/Campaigns/CreateCampaign/components/RecipientSection/RecipientsSection";
import DesignSection from "screens/Campaigns/CreateCampaign/components/DesignSection";
import DesignEmail from "screens/Campaigns/CreateCampaign/components/DesignEmail";
import { useEffect, useState } from "react";
import { CampaignContext } from "screens/Campaigns/CreateCampaign/Context";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { cloneDeep } from "lodash";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import Schedule from "screens/Campaigns/CreateCampaign/components/Schedule";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import TestModal from "screens/Campaigns/CreateCampaign/components/TestModal";
import { SubmitCampaign } from "types/Campaign";
import { initialSubmitCampaignFields } from "../Constants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useLocation } from "react-router-dom";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import {
  fetchApprovedDomains,
  getWholeCampaignDetails,
  submitRegularCampaign,
} from "screens/Campaigns/CampaignService";
import AttachmentSection from "../components/AttachmentSection";

const ResendCampaign = () => {
  useTitle(title.CREATE_RESEND_CAMPAIGN);
  const urlParams = new URLSearchParams(useLocation().search);
  const campaignId = urlParams.get("id");
  const [isDesignEmailModalOpen, setIsDesignEmailModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [managingStep, setManagingStep] = useState<string>("");
  const [submitCampaign, setSubmitCampaign] = useState<SubmitCampaign>(
    initialSubmitCampaignFields("Regular") as SubmitCampaign
  );
  const [editName, setEditName] = useState(false);
  const [formFields, setFormFields] = useState({
    name: {
      value: "",
      error: "",
    },
  });
  const [design, setDesign] = useState({
    content: submitCampaign.campaign.content ?? "",
    contentPlain: "",
    contentJson: submitCampaign.campaign.contentJson ?? {},
  });
  const [schedule, setSchedule] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loadingCampaignInfo, setLoadingCampaignInfo] =
    useState<boolean>(false);

  useEffect(() => {
    if (campaignId) {
      retrieveCampaignDetails();
    } else {
      history.push(viewpaths.campaignsViewPath);
    }
  }, [campaignId]);

  const retrieveCampaignDetails = async () => {
    try {
      setLoadingCampaignInfo(true);
      const [campaignResponse, domains] = await Promise.all([
        getWholeCampaignDetails(parseInt(campaignId!)),
        fetchApprovedDomains(),
      ]);
      const campaign = initialSubmitCampaignFields("Regular", campaignResponse);
      campaign.campaign.contentJson =
        typeof campaign.campaign.contentJson === "object"
          ? campaign.campaign.contentJson
          : JSON.parse(campaign?.campaign?.contentJson ?? {});
      if (domains.length === 1) {
        campaign.campaignData.domainName = domains[0];
      }
      campaign.campaign.id = 0;
      campaign.campaign.parentId = "";
      campaign.campaign.name = "Resend: " + campaign.campaign.name;
      campaign.campaignSegments.recipients = 0;
      campaign.campaignSegments.segmentTypeId = 1;
      campaign.campaignSegments.segmentIds = [];
      campaign.campaign.subject = "";
      setSubmitCampaign(campaign);
      setLoadingCampaignInfo(false);
      setDesign({
        content: campaign.campaign.content,
        contentJson: campaign.campaign.contentJson,
        contentPlain: campaign.campaign.contentPlain ?? "",
      });
    } catch (error: any) {
      setLoadingCampaignInfo(false);
      handleErrorMessage(error);
    }
  };

  const saveCampaignName = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSubmitCampaign({
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        name: formFields.name.value.trim(),
      },
    });
    setSaving(true);
    setEditName(false);
    setSaving(false);
  };

  const getHeader = () => {
    return (
      <Stack direction="row" px={2} justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Back to campaigns">
            <IconButton
              onClick={() =>
                history.push(
                  `${
                    viewpaths.campaignsViewPath
                  }?page=${1}&rowsPerPage=${10}&status=${
                    submitCampaign.campaign.status
                  }`
                )
              }
            >
              <SvgIcon>
                <KeyboardBackspaceIcon fontSize="small" />
              </SvgIcon>
            </IconButton>
          </Tooltip>
          {editName ? (
            <TextField
              size="small"
              variant="outlined"
              placeholder="Campaign Name"
              value={formFields.name.value}
              onChange={(event: any) => {
                setFormFields({
                  ...formFields,
                  name: {
                    value: event.target.value,
                    error: "",
                  },
                });
              }}
              required
              error={isTruthy(formFields.name.error)}
            />
          ) : (
            <Tooltip title={submitCampaign.campaign.name.trim()} arrow>
              <Typography
                color="text.primary"
                fontSize={"1.375rem"}
                fontWeight={600}
                noWrap
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                maxWidth="300px"
                display="block"
              >
                {submitCampaign.campaign.name.trim()}
              </Typography>
            </Tooltip>
          )}
          {editName ? (
            <>
              <IconButton onClick={saveCampaignName}>
                <Tooltip title="Save" arrow>
                  <SvgIcon>
                    <CheckIcon fontSize="small" />
                  </SvgIcon>
                </Tooltip>
              </IconButton>
              <IconButton onClick={() => setEditName(false)}>
                <Tooltip title="Cancel" arrow>
                  <SvgIcon>
                    <CloseIcon fontSize="small" />
                  </SvgIcon>
                </Tooltip>
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={() => {
                setFormFields({
                  ...formFields,
                  name: {
                    error: "",
                    value: submitCampaign.campaign.name,
                  },
                });
                setEditName(true);
              }}
            >
              <Tooltip title="Edit" arrow>
                <SvgIcon>
                  <ModeEditIcon fontSize="small" />
                </SvgIcon>
              </Tooltip>
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            disabled={
              !submitCampaign.campaign.fromIdEmail ||
              !submitCampaign.campaign.subject ||
              !submitCampaign.campaign.content ||
              !submitCampaign.campaignData.domainName
            }
            onClick={() => setIsTestModalOpen(true)}
          >
            Send test email
          </Button>
          <Button
            variant="contained"
            disabled={
              submitCampaign.campaignSegments.segmentIds.length === 0 ||
              !submitCampaign.campaign.fromIdEmail ||
              !submitCampaign.campaign.subject ||
              !submitCampaign.campaignData.domainName ||
              !submitCampaign.campaign.content
            }
            onClick={() => setSchedule(true)}
          >
            Schedule
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getBody = () => {
    return (
      <Card>
        <Stack spacing={4} py={4} divider={<Divider />}>
          <FromSection />
          <RecipientsSection />
          <SubjectSection />
          <DesignSection
            setIsDesignEmailModalOpen={setIsDesignEmailModalOpen}
            design={design}
            showEdit={false}
          />
          <AttachmentSection />
        </Stack>
      </Card>
    );
  };

  const doneEditing = async (content: any) => {
    setSaving(true);
    const campaign = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        content: content.content,
        contentJson: content.contentJson,
        contentPlain: content.contentPlain,
      },
    };
    setDesign(content);
    setIsDesignEmailModalOpen(false);
    setSubmitCampaign(campaign);
    setSaving(false);
  };

  const getDesignEmailModal = () => {
    return (
      <DesignEmail
        handleDialogClose={() => setIsDesignEmailModalOpen(false)}
        isDialogOpen={isDesignEmailModalOpen}
        doneEditing={doneEditing}
        design={design}
        setDesign={setDesign}
      />
    );
  };

  const getTestModal = () => {
    return (
      <TestModal
        handleDialogClose={() => setIsTestModalOpen(false)}
        isDialogOpen={isTestModalOpen}
      />
    );
  };
  const handleSubmission = async (submitCampaign: any, scheduleTime: any) => {
    try {
      const updatedCampaign = {
        ...submitCampaign,
        campaignData: {
          ...submitCampaign.campaignData,
          messageId: "",
        },
      };
      await submitRegularCampaign(updatedCampaign, scheduleTime);
    } catch (error: any) {
      throw error;
    }
  };
  const getScheduleModal = () => {
    return (
      <Schedule
        handleDialogClose={() => setSchedule(false)}
        isDialogOpen={schedule}
        handleSubmission={handleSubmission}
      />
    );
  };

  const getPage = () => {
    if (loadingCampaignInfo) {
      return (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Skeleton animation="wave" variant="text" height={45} width={200} />
            <Skeleton animation="wave" variant="text" height={45} width={200} />
          </Stack>
          <Skeleton animation="wave" variant="rounded" height={125} />
          <Skeleton animation="wave" variant="rounded" height={125} />
        </Stack>
      );
    }
    return (
      <Stack spacing={4}>
        {getHeader()}
        {getBody()}
      </Stack>
    );
  };

  const saveAsDraft = () => {};

  return (
    <CampaignContext.Provider
      value={{
        submitCampaign,
        setSubmitCampaign,
        managingStep,
        setManagingStep,
        saveAsDraft,
      }}
    >
      <CenteredAuthenticatedLayout>
        <Container maxWidth="md">{getPage()}</Container>
      </CenteredAuthenticatedLayout>
      {getDesignEmailModal()}
      {getScheduleModal()}
      {getTestModal()}
      <CustomLoader isLoading={saving} />
    </CampaignContext.Provider>
  );
};

export default ResendCampaign;
